import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useScroll, useMotionValueEvent } from "framer-motion";
import { Compass, PenLine, FileText, BadgeCheck, KeyRound } from "lucide-react";
import FadeIn, { SectionHeading } from "./FadeIn";
import { useInViewMount } from "./three/shared";

const MiniObject = lazy(() => import("./three/MiniObject"));

const STEPS = [
  { num: "01", title: "DISCOVER", icon: Compass, obj: "compass", text: "A private conversation about your goals, finances and timeline — no pressure, no jargon." },
  { num: "02", title: "STRATEGIZE", icon: PenLine, obj: "model", text: "We compare options across dozens of lenders and design the structure that fits you best." },
  { num: "03", title: "APPLY", icon: FileText, obj: "doc", text: "Your application and documents are prepared, packaged and submitted on your behalf." },
  { num: "04", title: "APPROVE", icon: BadgeCheck, obj: "seal", text: "We negotiate terms and conditions directly with lenders to secure your approval." },
  { num: "05", title: "CLOSE", icon: KeyRound, obj: "key", text: "From signing to keys in hand — we coordinate every detail through completion." },
];

export default function Process({ onLead }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(-1);
  const [render3D, setRender3D] = useState(false);
  const inView = useInViewMount(ref);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const check = () => setRender3D(!reduced && !window.matchMedia("(max-width: 767px)").matches);
    check();
    window.addEventListener("resize", check, { passive: true });
    return () => window.removeEventListener("resize", check);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.98", "end 0.3"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const next = v <= 0.02 ? -1 : Math.round(v * 4 * 20) / 20;
    setPos((prev) => (prev === next ? prev : next));
  });

  const intensity = (i) => (pos < 0 ? 0 : Math.min(Math.max(1.18 - Math.abs(pos - i), 0.06), 1));
  const lineScale = pos < 0 ? 0 : Math.min(pos / 4, 1);

  return (
    <section id="process" data-testid="process-section" className="py-24 lg:py-36 bg-charcoal/95">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          align="center"
          label="The Process"
          title="A Seamless Experience From Start To Finish"
        />

        <div className="mt-20 relative" ref={ref}>
          <div className="hidden lg:block absolute top-8 inset-x-0 h-px bg-gradient-to-r from-transparent via-bronze/40 to-transparent" />
          <div
            className="hidden lg:block absolute top-8 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold/80 to-transparent origin-left transition-transform duration-300 ease-out"
            style={{ transform: `scaleX(${lineScale})` }}
            data-testid="process-progress-line"
          />
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-6">
            {STEPS.map((s, i) => {
              const f = intensity(i);
              return (
                <FadeIn key={s.num} delay={i * 0.12}>
                  <div data-testid={`process-step-${i}`} className="relative text-center lg:text-left flex lg:block gap-6">
                    <div
                      className="relative z-10 shrink-0 w-16 h-16 rounded-full border bg-charcoal flex items-center justify-center mx-auto lg:mx-0 overflow-hidden transition-[border-color] duration-500"
                      style={{
                        borderColor: `rgba(229,192,123,${0.18 + f * 0.7})`,
                        boxShadow: `0 0 ${f * 52}px rgba(229,192,123,${f * 0.42})`,
                      }}
                    >
                      {render3D && inView ? (
                        <Suspense fallback={<s.icon size={20} strokeWidth={1.25} className="text-gold" />}>
                          <MiniObject type={s.obj} intensity={f} />
                        </Suspense>
                      ) : (
                        <s.icon size={20} strokeWidth={1.25} className="text-gold" />
                      )}
                    </div>
                    <div className="transition-opacity duration-500" style={{ opacity: 0.28 + f * 0.72 }}>
                      <p
                        className="font-serif text-4xl mt-5 transition-colors duration-700"
                        style={{ color: `rgba(229,192,123,${0.22 + f * 0.5})` }}
                      >
                        {s.num}
                      </p>
                      <h3
                        className="text-sm tracking-[0.24em] font-medium mt-2 mb-3 transition-colors duration-700"
                        style={{ color: f > 0.6 ? "#E5C07B" : "#FDFBF7" }}
                      >
                        {s.title}
                      </h3>
                      <p className="text-sm text-ivory/50 font-light leading-relaxed max-w-xs mx-auto lg:mx-0">{s.text}</p>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>

        <FadeIn className="mt-24 text-center">
          <p className="gold-label mb-5">The Journey Ends At Your Door</p>
          <p data-testid="process-closing-line" className="font-serif text-3xl md:text-4xl text-ivory tracking-tight">
            FROM STRATEGY <span className="gold-text-gradient italic">TO THE KEYS.</span>
          </p>
          <button
            data-testid="process-book-consultation-btn"
            onClick={() => onLead && onLead("consultation")}
            className="mt-9 px-10 py-4 text-xs tracking-[0.22em] font-semibold text-charcoal bg-gold hover:bg-champagne transition-colors duration-300 rounded-sm"
          >
            BOOK A CONSULTATION
          </button>
        </FadeIn>
      </div>
    </section>
  );
}
